/**
 * Passwordless login screen — OTP (one-time code) flow.
 *
 * UX is two steps:
 *   1. enter email → POST /auth/otp/request → email "Sending…"
 *   2. enter the 6-digit code that arrives by email → POST /auth/otp/verify
 *
 * A Google button is kept beside the form for users who prefer
 * social sign-in. There is no password field — that flow lives on
 * `LoginScreenEnhanced.tsx` for now and is no longer linked from the public
 * routes after this redesign.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Linking,
  Platform,
  ScrollView,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { MaterialIcons } from '@expo/vector-icons';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';

import { authAPI } from '../../services/api';
import GoogleSignInWeb from '../../components/auth/GoogleSignInWeb';
import AppHeaderBar from '../../components/layout/AppHeaderBar';
import { useTranslation } from '../../hooks/useTranslation';
import { trackAuthEvent } from '../../services/analytics';
import { analyticsErrorCode } from '../../services/analyticsPrivacy';
import { forgetSignInEmail, rememberSignInEmail } from './magicLinkConsume';
import { brand } from '../../design-system/theme/tokens';

const COLORS = {
  primary: brand.primary,
  background: '#FFFFFF',
  surface: '#F9FAFB',
  border: '#E5E7EB',
  error: '#DC2626',
  text: { primary: '#111827', secondary: '#4B5563', muted: '#9CA3AF' },
};

const landingBase = (process.env.EXPO_PUBLIC_LANDING_URL || 'https://mentorcoaching.ai').replace(/\/$/, '');
const TERMS_URL = `${landingBase}/terms`;
const PRIVACY_URL = `${landingBase}/privacy`;

const RESEND_COOLDOWN_SECONDS = 30;
const OTP_LENGTH = 6;

interface Props {
  onLoginSuccess?: () => void;
}

const isValidEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const getOtpErrorCode = (err: any): string | null => {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === 'object' && typeof detail.code === 'string') {
    return detail.code;
  }
  return null;
};

export default function PasswordlessLoginScreen({ onLoginSuccess }: Props) {
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();

  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);

  const codeInputRef = useRef<TextInput | null>(null);
  const verifyInFlightRef = useRef(false);

  useEffect(() => {
    trackAuthEvent('passwordless_login_screen_viewed');
  }, []);

  // Web-only: honor ``?email=…&otp_sent=1`` from the landing page so the
  // user lands directly on the OTP entry step. The landing modal pre-fires
  // the OTP request, so we just skip step 1 and show the code field.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const url = new URL(window.location.href);
      const prefillEmail = url.searchParams.get('email');
      const otpSent = url.searchParams.get('otp_sent');
      if (prefillEmail) {
        setEmail(prefillEmail);
        if (otpSent === '1') {
          setStep('code');
          setResendIn(RESEND_COOLDOWN_SECONDS);
          // Brief grace period, then focus the code input
          setTimeout(() => codeInputRef.current?.focus?.(), 150);
        }
        // Strip the params so a refresh doesn't replay the side-effects and the
        // email doesn't linger in the address bar (or in later analytics URLs).
        url.searchParams.delete('otp_sent');
        url.searchParams.delete('email');
        window.history.replaceState({}, document.title, url.toString());
      }
    } catch {
      /* ignore */
    }
  }, []);

  // Resend cooldown timer
  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  const trimmedEmail = useMemo(() => email.trim().toLowerCase(), [email]);

  const getOtpErrorMessage = (err: any) => {
    // Registration-disabled returns a plain-string 403 detail with no code;
    // without this it would surface as the misleading "wrong code" message.
    const detail = err?.response?.data?.detail;
    if (
      err?.response?.status === 403 &&
      typeof detail === 'string' &&
      detail.toLowerCase().includes('registration')
    ) {
      return t('auth.passwordless.login.registrationDisabled');
    }
    switch (getOtpErrorCode(err)) {
      case 'invalid_format':
        return t('auth.passwordless.login.invalidCodeFormat');
      case 'no_active_code':
        return t('auth.passwordless.login.noActiveCode');
      case 'expired':
        return t('auth.passwordless.login.expiredCode');
      case 'too_many_attempts':
        return t('auth.passwordless.login.tooManyAttempts');
      case 'locked':
        return t('auth.passwordless.login.otpLocked');
      case 'incorrect':
        return t('auth.passwordless.login.incorrectCode');
      case 'account_inactive':
        return t('auth.passwordless.login.accountInactive');
      default:
        return t('auth.passwordless.login.wrongCode');
    }
  };

  const requestCode = async () => {
    setError(null);
    if (!isValidEmail(trimmedEmail)) {
      setError(t('auth.passwordless.login.invalidEmail'));
      return;
    }
    setLoading(true);
    try {
      trackAuthEvent('otp_requested');
      const response = await authAPI.requestOtp(trimmedEmail, undefined);
      rememberSignInEmail(trimmedEmail);
      const retryAfter = Math.max(Number(response?.retry_after_seconds || 0), 0);
      setStep('code');
      setResendIn(retryAfter || RESEND_COOLDOWN_SECONDS);
      if (response?.sent !== false) {
        setCode('');
        // Defer focus until after render
        setTimeout(() => codeInputRef.current?.focus?.(), 100);
      } else {
        setError(t('auth.passwordless.login.resendWait', { seconds: retryAfter || RESEND_COOLDOWN_SECONDS }));
      }
    } catch (err: any) {
      setError(t('auth.passwordless.login.sendCodeError'));
      trackAuthEvent('otp_request_failed', { error_code: analyticsErrorCode(err) });
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (codeToVerify: string) => {
    if (codeToVerify.length !== OTP_LENGTH) return;
    if (verifyInFlightRef.current) return;
    setError(null);
    setLoading(true);
    verifyInFlightRef.current = true;
    try {
      trackAuthEvent('otp_verify_attempted');
      const res = await authAPI.verifyOtp(trimmedEmail, codeToVerify);
      // signup_completed for OTP sign-ups is captured server-side (ONB-20).
      trackAuthEvent('otp_verify_succeeded', { is_new_account: !!res?.is_new_account });
      forgetSignInEmail();
      onLoginSuccess?.();
    } catch (err: any) {
      setError(getOtpErrorMessage(err));
      setCode('');
      trackAuthEvent('otp_verify_failed', {
        error_code: getOtpErrorCode(err) || analyticsErrorCode(err),
      });
    } finally {
      verifyInFlightRef.current = false;
      setLoading(false);
    }
  };

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: COLORS.surface, paddingTop: insets.top }}
    >
      {/* Full-width edge-to-edge header bar (logo + lang + Help). */}
      <AppHeaderBar />
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={{ flex: 1 }}
      >
        <ScrollView
          contentContainerStyle={{
            flexGrow: 1,
            paddingHorizontal: 24,
            paddingTop: 40,
            paddingBottom: 32,
          }}
          keyboardShouldPersistTaps="handled"
        >
          <View style={{ width: '100%', maxWidth: 540, alignSelf: 'center' }}>
            <Card style={{ backgroundColor: COLORS.background, borderRadius: 16, borderWidth: 1, borderColor: COLORS.border }}>
              <CardContent style={{ padding: 28 }}>
                {/* Branded logotype: tells the user where they are at a
                    glance. The H1 below can drop the brand name in favour
                    of just the action verb (Sign in / Giriş yap). */}
                <View
                  className="self-center mb-4"
                  style={{ width: '70%', maxWidth: 240, aspectRatio: 12439 / 4268 }}
                >
                  <Image
                    source={require('../../../assets/logotype-web.png')}
                    style={{ width: '100%', height: '100%' }}
                    resizeMode="contain"
                    accessibilityLabel="Mentor Coaching AI"
                  />
                </View>

                {/* Badge pill */}
                <View
                  className="self-center flex-row items-center px-4 py-2 mb-4 rounded-full"
                  style={{ backgroundColor: '#EFF6FF' }}
                >
                  <MaterialIcons name="lock" size={14} color={COLORS.primary} />
                  <Text style={{ color: COLORS.primary, fontSize: 13, fontWeight: '600', marginLeft: 6 }}>
                    {t('auth.passwordless.login.badge')}
                  </Text>
                </View>

                {step === 'email' ? (
                  <>
                    <Text
                      className="text-center"
                      style={{ color: COLORS.text.primary, fontSize: 22, fontWeight: '700', marginBottom: 8 }}
                    >
                      {t('auth.passwordless.login.title')}
                    </Text>
                    <Text
                      className="text-center"
                      style={{ color: COLORS.text.secondary, fontSize: 14, lineHeight: 20, marginBottom: 24 }}
                    >
                      {t('auth.passwordless.login.subtitle')}
                    </Text>

                    <Text style={{ color: COLORS.text.primary, fontSize: 13, fontWeight: '500', marginBottom: 8 }}>
                      {t('auth.passwordless.login.email')}
                    </Text>
                    <View
                      className="flex-row items-center px-4 mb-3 rounded-lg"
                      style={{ backgroundColor: COLORS.background, borderWidth: 1, borderColor: COLORS.border, height: 48 }}
                    >
                      <MaterialIcons name="email" size={20} color={COLORS.text.muted} />
                      <TextInput
                        className="flex-1 ml-3 text-base"
                        style={{ color: COLORS.text.primary }}
                        placeholder={t('auth.passwordless.login.emailPlaceholder')}
                        placeholderTextColor={COLORS.text.muted}
                        value={email}
                        onChangeText={(v) => {
                          setEmail(v);
                          if (error) setError(null);
                        }}
                        keyboardType="email-address"
                        autoCapitalize="none"
                        autoCorrect={false}
                        editable={!loading}
                        onSubmitEditing={requestCode}
                        returnKeyType="send"
                      />
                    </View>

                    {error && (
                      <Text style={{ color: COLORS.error, fontSize: 13, marginBottom: 12 }}>{error}</Text>
                    )}

                    <Button
                      variant="default"
                      size="default"
                      onPress={requestCode}
                      disabled={loading || !email}
                      className="mb-4"
                    >
                      {loading ? (
                        <View className="flex-row items-center">
                          <ActivityIndicator size="small" color="white" />
                          <Text className="text-white font-semibold ml-2">
                            {t('auth.passwordless.login.sending')}
                          </Text>
                        </View>
                      ) : (
                        <Text className="text-white font-semibold">
                          {t('auth.passwordless.login.sendCode')}
                        </Text>
                      )}
                    </Button>
                  </>
                ) : (
                  <>
                    <Text
                      className="text-center"
                      style={{ color: COLORS.text.primary, fontSize: 22, fontWeight: '700', marginBottom: 8 }}
                    >
                      {t('auth.passwordless.login.codeSentTitle')}
                    </Text>
                    <Text
                      className="text-center"
                      style={{ color: COLORS.text.secondary, fontSize: 14, lineHeight: 20, marginBottom: 20 }}
                    >
                      {t('auth.passwordless.login.codeSentSubtitle', { email: trimmedEmail })}
                    </Text>

                    {/* Code input — single field with letter spacing for that big-digits look */}
                    <View
                      className="self-center px-4 mb-3 rounded-lg flex-row items-center justify-center"
                      style={{
                        backgroundColor: COLORS.background,
                        borderWidth: 1,
                        borderColor: error ? COLORS.error : COLORS.border,
                        height: 64,
                        width: '100%',
                      }}
                    >
                      <TextInput
                        ref={codeInputRef}
                        value={code}
                        onChangeText={(v) => {
                          const digits = v.replace(/\D/g, '').slice(0, OTP_LENGTH);
                          setCode(digits);
                          if (error) setError(null);
                          if (digits.length === OTP_LENGTH) verifyCode(digits);
                        }}
                        keyboardType="number-pad"
                        inputMode="numeric"
                        autoComplete={Platform.OS === 'web' ? ('one-time-code' as any) : 'sms-otp'}
                        textContentType="oneTimeCode"
                        maxLength={OTP_LENGTH}
                        editable={!loading}
                        placeholder="••••••"
                        placeholderTextColor={COLORS.text.muted}
                        style={{
                          flex: 1,
                          fontSize: 28,
                          fontWeight: '700',
                          letterSpacing: 12,
                          textAlign: 'center',
                          color: COLORS.text.primary,
                          fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
                        }}
                      />
                    </View>

                    <Text className="text-center" style={{ color: COLORS.text.muted, fontSize: 12, marginBottom: 16 }}>
                      <MaterialIcons name="schedule" size={12} color={COLORS.text.muted} />{'  '}
                      {t('auth.passwordless.login.codeExpiresIn')}
                    </Text>

                    {error && (
                      <Text className="text-center" style={{ color: COLORS.error, fontSize: 13, marginBottom: 12 }}>
                        {error}
                      </Text>
                    )}

                    <Button
                      variant="default"
                      size="default"
                      onPress={() => verifyCode(code)}
                      disabled={loading || code.length !== OTP_LENGTH}
                      className="mb-3"
                    >
                      {loading ? (
                        <View className="flex-row items-center">
                          <ActivityIndicator size="small" color="white" />
                          <Text className="text-white font-semibold ml-2">
                            {t('auth.passwordless.login.verifying')}
                          </Text>
                        </View>
                      ) : (
                        <Text className="text-white font-semibold">
                          {t('auth.passwordless.login.verify')}
                        </Text>
                      )}
                    </Button>

                    <View className="flex-row justify-between items-center mt-2">
                      <TouchableOpacity
                        onPress={() => {
                          setStep('email');
                          setCode('');
                          setError(null);
                        }}
                        accessibilityRole="button"
                      >
                        <Text style={{ color: COLORS.text.secondary, fontSize: 13 }}>
                          {t('auth.passwordless.login.changeEmail')}
                        </Text>
                      </TouchableOpacity>

                      <TouchableOpacity
                        onPress={requestCode}
                        disabled={resendIn > 0 || loading}
                        accessibilityRole="button"
                      >
                        <Text
                          style={{
                            color: resendIn > 0 ? COLORS.text.muted : COLORS.primary,
                            fontSize: 13,
                            fontWeight: '600',
                          }}
                        >
                          {resendIn > 0
                            ? t('auth.passwordless.login.resendCooldown', { seconds: resendIn })
                            : t('auth.passwordless.login.resend')}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  </>
                )}

                {/* Divider + OAuth */}
                <View className="flex-row items-center my-6">
                  <View className="flex-1 h-px" style={{ backgroundColor: COLORS.border }} />
                  <Text className="mx-4 text-sm" style={{ color: COLORS.text.muted }}>
                    {t('auth.passwordless.login.or')}
                  </Text>
                  <View className="flex-1 h-px" style={{ backgroundColor: COLORS.border }} />
                </View>

                <View style={{ gap: 10 }}>
                  <View className="items-center">
                    <GoogleSignInWeb
                      onSuccess={() => {
                        trackAuthEvent('login_succeeded', { method: 'google' });
                        onLoginSuccess?.();
                      }}
                      onError={(msg) => {
                        trackAuthEvent('login_failed', { method: 'google', error_code: 'google_sign_in_failed' });
                        setError(msg);
                      }}
                    />
                  </View>
                </View>
              </CardContent>
            </Card>

            <View className="items-center py-4">
              <Text style={{ color: COLORS.text.secondary, fontSize: 14 }}>
                {t('auth.passwordless.login.newAccountHint')}
              </Text>
            </View>

            <View className="items-center mt-2 mb-2">
              <Text className="text-xs text-center" style={{ color: COLORS.text.muted, lineHeight: 18 }}>
                <Text
                  className="text-xs underline"
                  style={{ color: COLORS.primary }}
                  onPress={() => Linking.openURL(TERMS_URL)}
                >
                  {t('profile.termsOfService')}
                </Text>
                {'  ·  '}
                <Text
                  className="text-xs underline"
                  style={{ color: COLORS.primary }}
                  onPress={() => Linking.openURL(PRIVACY_URL)}
                >
                  {t('profile.privacyPolicy')}
                </Text>
              </Text>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
